import React from 'react';
import { Clock, User, ArrowRight } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const Blog = () => {
  const { content, editing, update } = useCmsContent();
  const { blogPosts } = content;
  const categories = [...new Set(blogPosts.map(post => post.category))];
  const [selectedCategory, setSelectedCategory] = React.useState('__all__');

  const filteredPosts = selectedCategory === '__all__'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-brand-700 to-brand-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-light mb-4"><EditableText contentKey="pages.blog.heroTitle" /></h1>
          <p className="text-xl max-w-3xl mx-auto">
            <EditableText contentKey="pages.blog.heroBody" />
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {["__all__", ...categories].map((category) => {
              const categoryIndex = blogPosts.findIndex((post) => post.category === category);
              return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedCategory === category
                    ? 'bg-brand-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category === '__all__'
                  ? <EditableText contentKey="pages.blog.all" multiline={false} />
                  : <EditableText contentKey={`blogPosts.${categoryIndex}.category`} multiline={false} />}
              </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => {
              const postIndex = blogPosts.findIndex((candidate) => candidate.id === post.id);
              return (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <span className="bg-brand-100 text-brand-800 px-2 py-1 rounded text-xs font-medium">
                      <EditableText contentKey={`blogPosts.${postIndex}.category`} multiline={false} />
                    </span>
                    <span className="mx-2">•</span>
                    <Clock className="h-4 w-4 mr-1" />
                    <EditableText contentKey={`blogPosts.${postIndex}.readTime`} multiline={false} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                    <EditableText contentKey={`blogPosts.${postIndex}.title`} />
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3"><EditableText contentKey={`blogPosts.${postIndex}.excerpt`} /></p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <User className="h-4 w-4 mr-1" />
                      <EditableText contentKey={`blogPosts.${postIndex}.author`} multiline={false} />
                    </div>
                    <span className="text-brand-700 font-medium text-sm group-hover:text-brand-800">
                      <EditableText contentKey="pages.blog.readMore" multiline={false} /> <ArrowRight className="inline h-3 w-3" />
                    </span>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-brand-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light mb-4"><EditableText contentKey="pages.blog.newsletterTitle" /></h2>
          <p className="text-xl mb-8">
            <EditableText contentKey="pages.blog.newsletterBody" />
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder={editing ? '' : content.pages.blog.emailPlaceholder}
              value={editing ? content.pages.blog.emailPlaceholder : undefined}
              onChange={editing ? (event) => update('pages.blog.emailPlaceholder', event.target.value) : undefined}
              className="flex-1 px-4 py-3 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button className="px-6 py-3 bg-white text-brand-700 rounded-md hover:bg-gray-100 transition-colors font-medium">
              <EditableText contentKey="pages.blog.subscribe" multiline={false} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
